import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { FormControlLabel, Switch, IconButton, Menu, MenuItem, CircularProgress, Snackbar, Alert } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import AccordinButton from "components/AccordinButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CancelIcon from "@mui/icons-material/Cancel";
import ClickOutside from "components/OnClickOutside";
import ResizeAndDragabbleSlide from "components/ResizeAndDraggableSlides";
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import SlidesIcon from '@mui/icons-material/Slideshow';
import PrintIcon from '@mui/icons-material/Print';
import SettingsIcon from '@mui/icons-material/Settings';
import styled from "styled-components";
import { find, get } from "lodash";
import MessageRenderer from "components/MessageRenderer";

import { openSlideWidgetAction } from "store/slides/actions";
import { useCollectionWidgets } from "utils/methods/firebaseWidgets";
import { useCurrentUser } from "store/users/selectors";
import { v4 } from "uuid";
import SlideImageHighlighterWidget from "./slideWidgetHighlighter";

import { useOpenSlideWidget } from "store/slides/selectors";
import { debounceMath } from "utils/methods/math";
import usePermissionForUser from "components/hooks/permissions/usePermissionForUser";
import ExportToSlides from "./export_to_google_slides";
import { handleGetAllSlides } from "store/slides/thunk";
import SentryWrap from 'components/Sentry';
import { useIsTab, useMobileDetect } from "containers/chat/hooks";
import { getImageThumbPrimaryUrl } from "utils/methods/storageImageThumbSrcList";

const StyledSwitch = muiStyled(Switch)(({ theme }) => ({
  width: 43,
  height: 25,
  '&.Mui-checked': {
    transform: 'translateX(30px) !important',
  }
}));

const StyledFormControlLabel = muiStyled(FormControlLabel)(({ theme }) => ({
  margin: '5px',
  marginLeft: '16px',
  marginTop: '12px',
  width: 'fit-content',
  '& .MuiFormControlLabel-label': {
    textTransform: 'uppercase',
  }
}));
const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const StyledIconButton = muiStyled(IconButton)(({ theme }) => ({
  color: 'white',
  fontSize: 22,
  padding: '4px',
  borderRadius: '20px',
  transition: 'all 0.3s ease-in-out',
  transformOrigin: 'center',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  }
}));

const StyledCancelIcon = muiStyled(CancelIcon)(({ theme }) => ({
  marginLeft: '5px',
  position: 'absolute',
  right: 17,
  top: 10,
  fontSize: 23,
  color: 'red',
  background: 'white',
  borderRadius: 20,
  cursor: 'pointer',
}));

const NavigationArrow = muiStyled('div')(({ theme, direction }) => ({
  position: 'absolute',
  [direction]: '10px',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  borderRadius: '50%',
  cursor: 'pointer',
  zIndex: 10,
  width: 35,
  height: 35,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(direction === 'left' && {
    paddingLeft: 9,
  }),
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.8)',
  }
}));

const SlideWidgetComponent = ({ currentClass, sharableWidget }) => {
  const { open = false, bookId = "", pageId = "", data } = useOpenSlideWidget();
  const dispatch = useDispatch();
  const { createCollectionWidgets } = useCollectionWidgets();
  const currentUser = useCurrentUser();
  const { districtPermissionConfig } = usePermissionForUser();
  const {download_ppt = false } = districtPermissionConfig;
  const isTeacher = get(currentUser, "role") === "teacher";
  const isStudent = get(currentUser, "role") === "student";
  const [showAccordin, setShowAccordin] = useState(true);
  const [dimensions, setDimensions] = React.useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageConfiguration, setImageConfiguration] = useState([]);
  const [toogleButton, setToogleButton] = useState(false);
  const [toogleButtonSlides, setToogleButtonSlides] = useState(true);
  const [fontSize, setFontSize] = useState(16);
  const [isScrollingThumbnails, setIsScrollingThumbnails] = useState(false);
  const [thumbnailSize, setThumbnailSize] = useState(180); 
  const [isDragging, setIsDragging] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pptxBlob, setPptxBlob] = useState(null);
  const [alertState, setAlertState] = useState({ open: false, message: '', severity: 'error' });
  const isTablate = useIsTab();
  const isMobile = useMobileDetect();

  const showAlert = (message, severity = 'error') => {
    setAlertState({ open: true, message, severity });
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') return;
    setAlertState({ ...alertState, open: false });
  };

  const thumbnailRefs = useRef({});
  const sliderContainerRef = useRef(null);
  const shareClickTimeoutRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const isScrollingRef = useRef(false);
  const increaseFontSize = () => {
    setFontSize((prev) => (prev < 30 ? prev + 2 : prev));
  };
  const decreaseFontSize = () => {
    setFontSize((prev) => (prev > 12 ? prev - 2 : prev));
  };


  const filterShareSlide = find(
    sharableWidget,
    (obj) => obj?.widget_type === "widget_slide"
  );
  const getSlideData = useMemo(() => data ? data : ({}), [open, data]);

  const currentSlideData = useMemo(() => {
    const slideIndex = filterShareSlide?.data?.currentPage ?? currentSlide;
    if (getSlideData?.pages) {
      const validSlideIndex = getSlideData.pages[slideIndex] ? slideIndex : 0;
      return getSlideData.pages[validSlideIndex] || {};
    }

    return {};
  }, [currentSlide, getSlideData, pageId, bookId, filterShareSlide]);

  const handleClose = async () => {
    setToogleButton(false);
    setPptxBlob(null);
    handleDownloadMenuClose();
    handleExportMenuClose();
    handlePrintMenuClose();
    handleSettingsMenuClose();
    const closeShareData = {
      ...filterShareSlide,
      status: false,
      message: "widget-slide",
    };
    await dispatch(openSlideWidgetAction({ open: false }));
    if (filterShareSlide?.status) {
      createCollectionWidgets(closeShareData);
    }
  };

  const scrollToActiveThumbnail = (slideIndex) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    if (isScrollingRef.current) {
      return;
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      if (shareClickTimeoutRef.current) {
        return;
      }
      
      const thumbnailRef = thumbnailRefs.current[slideIndex];
      const container = sliderContainerRef.current;
      
      if (thumbnailRef && container) {
        isScrollingRef.current = true;
        
        requestAnimationFrame(() => {
          const thumbnailRect = thumbnailRef.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const scrollLeft = container.scrollLeft;
          const thumbnailLeft = thumbnailRect.left - containerRect.left + scrollLeft;
          const thumbnailWidth = thumbnailRect.width;
          const containerWidth = containerRect.width;
          
          const scrollPosition = Math.max(0, thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2));
          
          container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
          });
          
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 400);
        });
      }
    }, 150);
  };

  const handleActiveSlide = (index) => {
    const currentPageData = getSlideData?.pages?.[index];
    if (!currentPageData) {
      setToogleButton(false);
    } else {
      if (currentPageData?.notes) {
      } else {
        setToogleButton(false);
      }
      if (filterShareSlide?.id && currentPageData) {
        const dataShare = {
          icons: currentPageData.src,
          currentPage: index,
          pages: currentPageData,
          completeData: getSlideData
        };
        const editShareData = {
          ...filterShareSlide,
          data: dataShare,
        };
        createCollectionWidgets(editShareData);
      }
    }
    setCurrentSlide(index);
    scrollToActiveThumbnail(index);
  };


  const handleTouchShare = (event) => {
    const closeShareData = {
      ...filterShareSlide,
      status: false,
      message: "widget-slide",
    };
    event.preventDefault();
    if (filterShareSlide?.id) {
      createCollectionWidgets(closeShareData);
    }
    else {
      sharePPT();
    }
  };

  const sharePPT = () => {
    if (!filterShareSlide?.id) {
      const getCurrentPage = getSlideData?.pages[currentSlide];
      const shareData = {
        id: v4(),
        class_id: currentClass?.id,
        created_by: currentUser?.id,
        created_at: new Date(),
        widget_type: "widget_slide",
        status: true,
        data: {
          icons: getCurrentPage.src,
          currentPage: currentSlide,
          pages: getCurrentPage,
          completeData: getSlideData
        },
      };
      createCollectionWidgets(shareData);
    }
  };

  const sharedData = filterShareSlide?.data || {};
  const handleNextSlide = () => {
    if (currentSlide < getSlideData?.pages?.length - 1) {
      const nextSlide = currentSlide + 1;
      if (filterShareSlide?.id) {
        const getCurrentPage = getSlideData?.pages[nextSlide];
        const dataShare = {
          icons: getCurrentPage.src,
          currentPage: nextSlide,
          pages: getCurrentPage,
          completeData: getSlideData
        };
        const editShareData = {
          ...filterShareSlide,
          data: dataShare,
        };
        createCollectionWidgets(editShareData);
      }
      setCurrentSlide(nextSlide);
      scrollToActiveThumbnail(nextSlide);
    }
    setIsScrollingThumbnails(false);
  };

  const handlePrevSlide = () => {
    if (currentSlide > 0) {
      const prevSlide = currentSlide - 1;
      if (filterShareSlide?.id) {
        const getCurrentPage = getSlideData?.pages[prevSlide];
        const dataShare = {
          icons: getCurrentPage.src,
          currentPage: prevSlide,
          pages: getCurrentPage,
          completeData: getSlideData
        };
        const editShareData = {
          ...filterShareSlide,
          data: dataShare,
        };
        createCollectionWidgets(editShareData);
      }
      setCurrentSlide(prevSlide);
      scrollToActiveThumbnail(prevSlide);
    }
    setIsScrollingThumbnails(false);
  };

  const onNotesClick = () => {

  }

  const handleDownloadMenuOpen = (event) => {
    handleExportMenuClose();
    handlePrintMenuClose();
    handleSettingsMenuClose();
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleExportMenuOpen = (event) => {
    handleDownloadMenuClose();
    handlePrintMenuClose();
    handleSettingsMenuClose();
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handlePrintMenuOpen = (event) => {
    handleDownloadMenuClose();
    handleExportMenuClose();
    handleSettingsMenuClose();
    setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handleSettingsMenuOpen = (event) => {
    handleDownloadMenuClose();
    handleExportMenuClose();
    handlePrintMenuClose();
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const [exportIncludeNotes, setExportIncludeNotes] = useState(null);
  const exportToSlidesRef = useRef(null);

  useEffect(() => {
    if (exportIncludeNotes !== null && exportToSlidesRef.current) {
      exportToSlidesRef.current.triggerExport(exportIncludeNotes);
      setExportIncludeNotes(null);
    }
  }, [exportIncludeNotes]);

  const loadImageAsBase64 = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error loading image:", error);
      return null;
    }
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const parseHtmlToPptText = (html) => {
    if (!html) return [];
    
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    
    const textRuns = [];
    const TEXT_NODE = 3;
    const ELEMENT_NODE = 1;
    
    const extractTextFromNode = (node) => {
      if (!node) return "";
      if (node.nodeType === TEXT_NODE) {
        return node.textContent.trim();
      }
      if (node.nodeType === ELEMENT_NODE) {
        return Array.from(node.childNodes)
          .map(child => extractTextFromNode(child))
          .filter(Boolean)
          .join(" ");
      }
      return "";
    };
    
    const processNode = (node, indentLevel = 0, listType = null, index = 1) => {
      if (!node) return;
    
      if (node.nodeType === TEXT_NODE) {
        const text = node.textContent.trim();
        if (text && !listType) {
          textRuns.push({
            text: text + " ",
            options: {
              fontSize: 11,
              color: "000000",
              fontFace: "Arial",
            },
          });
        }
        return;
      }
    
      if (node.nodeType !== ELEMENT_NODE) return;
      const tagName = node.tagName?.toLowerCase();
    
      if (tagName === "ul" || tagName === "ol") {
        const newListType = tagName === "ol" ? "number" : "bullet";
        Array.from(node.childNodes).forEach((child, idx) => {
          processNode(child, indentLevel, newListType, idx + 1);
        });
      } else if (tagName === "li") {
        const liText = extractTextFromNode(node);
        if (liText) {
          textRuns.push({
            text: "\n",
            options: { breakLine: true },
          });
    
          const prefix =
            listType === "number" ? `${index}. ` : listType === "bullet" ? "• " : "";
    
          textRuns.push({
            text: prefix + liText,
            options: {
              indentLevel,
              fontSize: 11,
              color: "000000",
              fontFace: "Arial",
            },
          });
        }
    
        const nestedLists = Array.from(node.childNodes).filter(
          (child) =>
            child.tagName?.toLowerCase() === "ul" ||
            child.tagName?.toLowerCase() === "ol"
        );
        nestedLists.forEach((nestedList) => {
          processNode(nestedList, indentLevel + 1, nestedList.tagName.toLowerCase() === "ol" ? "number" : "bullet");
        });
      } else if (tagName === "p") {
        if (textRuns.length > 0 && !textRuns[textRuns.length - 1].options?.breakLine) {
          textRuns.push({ text: "\n", options: { breakLine: true } });
        }
        Array.from(node.childNodes).forEach((child) =>
          processNode(child, indentLevel, listType)
        );
        textRuns.push({ text: "\n", options: { breakLine: true } });
      } else if (/^h[1-6]$/.test(tagName)) {
        if (textRuns.length > 0 && !textRuns[textRuns.length - 1].options?.breakLine) {
          textRuns.push({ text: "\n", options: { breakLine: true } });
        }
        const headingText = node.textContent.trim();
        if (headingText) {
          const fontSizeMap = { h1: 18, h2: 16, h3: 14, h4: 13, h5: 12, h6: 11 };
          textRuns.push({
            text: headingText,
            options: {
              fontSize: fontSizeMap[tagName] || 14,
              color: "000000",
              fontFace: "Arial",
              bold: true,
            },
          });
          textRuns.push({ text: "\n", options: { breakLine: true } });
        }
      } else if (tagName === "strong" || tagName === "b") {
        const boldText = node.textContent.trim();
        if (boldText) {
          textRuns.push({
            text: boldText,
            options: {
              fontSize: 11,
              color: "000000",
              fontFace: "Arial",
              bold: true,
            },
          });
        }
      } else if (tagName === "em" || tagName === "i") {
        const italicText = node.textContent.trim();
        if (italicText) {
          textRuns.push({
            text: italicText,
            options: {
              fontSize: 11,
              color: "000000",
              fontFace: "Arial",
              italic: true,
            },
          });
        }
      } else if (tagName === "br") {
        textRuns.push({ text: "\n", options: { breakLine: true } });
      } else {
        Array.from(node.childNodes).forEach((child) =>
          processNode(child, indentLevel, listType)
        );
      }
    };
    
    
    Array.from(tmp.childNodes).forEach(child => {
      processNode(child, 0, false);
    });
    
    if (textRuns.length === 0) {
      const plainText = tmp.textContent?.trim();
      if (plainText) {
        textRuns.push({
          text: plainText,
          options: {
            fontSize: 11,
            color: "000000",
            fontFace: "Arial",
          }
        });
      }
    }
    
    return textRuns;
  };

  const generatePPT = async (includeNotes, shouldDownload = true) => {
    if (!getSlideData?.pages || getSlideData.pages.length === 0) {
      alert("No slides available to download");
      setIsDownloading(false);
      return null;
    }

    setIsDownloading(shouldDownload ? true : false);
    try {
      const pptxgen = (await import("pptxgenjs")).default;
      const presentation = new pptxgen();
      presentation.layout = "LAYOUT_16x9";

      for (let i = 0; i < getSlideData.pages.length; i++) {
        const slide = getSlideData.pages[i];
        
        if (!slide?.src) continue;

        const imageSlide = presentation.addSlide();
        
        const imageBase64 = await loadImageAsBase64(slide.src);
        if (imageBase64) {
          imageSlide.addImage({
            data: imageBase64,
            x: 0,
            y: 0,
            w: "100%",
            h: "100%",
          });
        }

        if (includeNotes && slide?.notes) {
          const textRuns = parseHtmlToPptText(slide.notes);
          
          if (textRuns && textRuns.length > 0) {
            const MAX_CHARS_PER_SLIDE = 450;
            const MAX_LINES_PER_SLIDE = 14;
            const HEADER_LINES = 4;
            let currentSlideRuns = [];
            let currentCharCount = 0;
            let currentLineCount = 0;
            let notesSlideIndex = 0;
        
            const createNotesSlide = (isContinuation = false) => {
              if (currentSlideRuns.length > 0) {
                const notesSlide = presentation.addSlide();
                
                const slideNumber = i + 1;
                const headerText = 'Notes';
                
                notesSlide.addText([
                  {
                    text: headerText,
                    options: {
                      fontSize: 13,
                      color: "333333",
                      fontFace: "Arial",
                      bold: true,
                    }
                  },
                  { text: "\n", options: { breakLine: true } },
                  {
                    text: "─".repeat(50),
                    options: {
                      fontSize: 10,
                      color: "666666",
                      fontFace: "Arial",
                    }
                  },
                  { text: "\n\n", options: { breakLine: true } },
                  ...currentSlideRuns
                ], {
                  x: 0.5,
                  y: 0.5,
                  w: 9,
                  h: 6,
                  align: "left",
                  valign: "top",
                  wrap: true,
                });
                
                currentSlideRuns = [];
                currentCharCount = 0;
                currentLineCount = 0;
                notesSlideIndex++;
              }
            };

            const countLines = (text) => {
              if (!text) return 0;
              const newlines = (text.match(/\n/g) || []).length;
              const avgCharsPerLine = 65;
              const estimatedLines = Math.ceil(text.length / avgCharsPerLine);
              return Math.max(newlines + 1, estimatedLines);
            };

            const splitTextRun = (run, maxLength) => {
              const chunks = [];
              let remainingText = run.text || "";
              
              while (remainingText.length > 0) {
                if (remainingText.length <= maxLength) {
                  chunks.push({
                    ...run,
                    text: remainingText
                  });
                  break;
                }
                
                let splitIndex = maxLength;
                
                const lastNewline = remainingText.lastIndexOf("\n", maxLength);
                const lastPeriod = remainingText.lastIndexOf(". ", maxLength);
                const lastSpace = remainingText.lastIndexOf(" ", maxLength);
                
                let preferredBreak = -1;
                
                if (lastNewline > maxLength * 0.5) {
                  preferredBreak = lastNewline;
                } else if (lastPeriod > maxLength * 0.6) {
                  preferredBreak = lastPeriod + 2;
                } else if (lastSpace > maxLength * 0.7) {
                  preferredBreak = lastSpace + 1;
                }
                
                if (preferredBreak > maxLength * 0.5) {
                  splitIndex = preferredBreak;
                }
                
                const chunkText = remainingText.substring(0, splitIndex);
                chunks.push({
                  ...run,
                  text: chunkText
                });
                
                remainingText = remainingText.substring(splitIndex);
              }
              
              return chunks;
            };
        
            for (const run of textRuns) {
              const textLength = run.text?.length || 0;
              const lineCount = countLines(run.text || "");
              
              const effectiveMaxChars = MAX_CHARS_PER_SLIDE;
              const effectiveMaxLines = MAX_LINES_PER_SLIDE - HEADER_LINES;
              
              if (textLength > effectiveMaxChars || lineCount > effectiveMaxLines) {
                const chunkSize = Math.floor(effectiveMaxChars * 0.85);
                const chunks = splitTextRun(run, chunkSize);
                
                for (const chunk of chunks) {
                  const chunkLength = chunk.text?.length || 0;
                  const chunkLines = countLines(chunk.text || "");
                  
                  if (chunkLength > effectiveMaxChars || chunkLines > effectiveMaxLines) {
                    if (currentSlideRuns.length > 0) {
                      createNotesSlide(notesSlideIndex > 0);
                    }
                    const smallerChunks = splitTextRun(chunk, Math.floor(effectiveMaxChars * 0.8));
                    for (const smallerChunk of smallerChunks) {
                      const smallerLength = smallerChunk.text?.length || 0;
                      const smallerLines = countLines(smallerChunk.text || "");
                      if (currentCharCount + smallerLength > effectiveMaxChars || 
                          currentLineCount + smallerLines > effectiveMaxLines) {
                        createNotesSlide(notesSlideIndex > 0);
                      }
                      currentSlideRuns.push(smallerChunk);
                      currentCharCount += smallerLength;
                      currentLineCount += smallerLines;
                    }
                  } else {
                    if (currentCharCount + chunkLength > effectiveMaxChars || 
                        currentLineCount + chunkLines > effectiveMaxLines) {
                      createNotesSlide(notesSlideIndex > 0);
                    }
                    currentSlideRuns.push(chunk);
                    currentCharCount += chunkLength;
                    currentLineCount += chunkLines;
                  }
                }
              } else {
                if (currentCharCount + textLength > effectiveMaxChars || 
                    currentLineCount + lineCount > effectiveMaxLines) {
                  createNotesSlide(notesSlideIndex > 0);
                }
                currentSlideRuns.push(run);
                currentCharCount += textLength;
                currentLineCount += lineCount;
              }
            }
        
            if (currentSlideRuns.length > 0) {
              createNotesSlide(notesSlideIndex > 0);
            }
          }
        }
        
      }

      const pptxBlob = await presentation.write("blob");

      if (shouldDownload) {
        const fileName = `Presentation_${new Date().getTime()}.pptx`;
        await presentation.writeFile({ fileName });
      }

      setPptxBlob(pptxBlob);
      handleDownloadMenuClose();
      setIsDownloading(false);
      return pptxBlob;
    } catch (error) {
      console.error("Error generating PPT:", error);
      alert("Error generating PPT. Please try again.");
      handleDownloadMenuClose();
      setIsDownloading(false);
      return null;
    }
  };

  const handleDownloadWithNotes = () => {
    generatePPT(true);
  };

  const handleDownloadWithoutNotes = () => {
    generatePPT(false);
  };

  const handlePrintWithNotes = () => {
    if (isMobile || isTablate) {
      showAlert("Print is not supported on this device!", 'error');
      return;
    }
    handlePrintMenuClose();
    printSlides(true);
  };

  const handlePrintWithoutNotes = () => {
    if (isMobile || isTablate) {
      showAlert("Print is not supported on this device!", 'error');
      return;
    }
    handlePrintMenuClose();
    printSlides(false);
  };

  const printSlides = async (includeNotes) => {
    if (!getSlideData?.pages || getSlideData.pages.length === 0) {
      showAlert("No slides available to print", 'error');
      return;
    }

    try {
      let printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print Slides</title>
          <style>
            @page {
              size: letter landscape;
              margin: 0;
            }
            @media print {
              @page {
                margin: 0 !important;
              }
              body {
                margin: 0 !important;
                padding: 20px !important;
              }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }
            .slide-image-container {
              margin: 0;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              page-break-inside: avoid;
            }
            .slide-image {
              max-width: 100%;
              height: auto;
              page-break-inside: avoid;
            }
            .notes-container {
              margin: 0;
              padding: 20px;
              font-size: 12px;
              line-height: 1.6;
              display: flex;
              flex-direction: column;
              page-break-inside: avoid;
            }
            .notes-title {
              font-weight: bold;
              font-size: 16px;
              margin-bottom: 15px;
            }
            .slide-number {
              font-size: 10px;
              color: #666;
              margin-bottom: 10px;
            }
            @media print {
              .slide-image-container {
                page-break-inside: avoid;
              }
              .notes-container {
                page-break-inside: avoid;
              }
              .slide-image {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
      `;

      const validSlides = getSlideData.pages.filter(slide => slide?.src);
      
      for (let i = 0; i < validSlides.length; i++) {
        const slide = validSlides[i];
        const isLastSlide = i === validSlides.length - 1;
        const hasNotes = includeNotes && slide?.notes && stripHtmlTags(slide.notes).trim();
        
        const slideBreakAfter = includeNotes && hasNotes ? 'always' : (isLastSlide ? 'auto' : 'always');
        
        printContent += `
          <div class="slide-image-container" style="page-break-after: ${slideBreakAfter};">
            <div class="slide-number">Slide ${i + 1} of ${validSlides.length}</div>
            <img src="${slide.src}" alt="Slide ${i + 1}" class="slide-image" />
          </div>
        `;

        if (hasNotes) {
          const notesBreakAfter = isLastSlide ? 'auto' : 'always';
          printContent += `
            <div class="notes-container" style="page-break-after: ${notesBreakAfter};">
              <div class="notes-title">Notes for Slide ${i + 1}:</div>
              <div>${slide.notes}</div>
            </div>
          `;
        }
      }

      printContent += `
        </body>
        </html>
      `;

      const blob = new Blob([printContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          const printWindow = iframe.contentWindow;
          printWindow.focus();
          
          const style = printWindow.document.createElement('style');
          style.textContent = `
            @page {
              margin: 0 !important;
            }
            @media print {
              @page {
                margin: 0 !important;
              }
              body {
                margin: 0 !important;
                padding: 20px !important;
              }
            }
          `;
          printWindow.document.head.appendChild(style);
          
          printWindow.print();
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            document.body.removeChild(iframe);
          }, 100);
        }, 250);
      };
    } catch (error) {
      console.error("Error printing slides:", error);
      showAlert("Error printing slides. Please try again.", 'error');
    }
  };

  // Add a function to determine screen size
  const getDefaultDimensions = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    if (width < 520) {
      const widgetWidth = width * 0.7;
      const widgetHeight = isStudent ? 198 : isScrollingThumbnails ? 450 : 397;
      return {
        x: (width - widgetWidth) / 2,
        y: Math.max(20, (height - widgetHeight) / 2),
        width: widgetWidth,
        height: widgetHeight,
      };
    }
    else if (width < 768) {
      return {
        x: 50,
        y: 50,
        width: 332,
        height: isStudent ? 198 : isScrollingThumbnails ? 450 : 397,
      };
    }
    else if (width >= 768 && width < 1024) {
      return {
        x: 50,
        y: 100,
        width: 704,
        height: isStudent ? 407 : isScrollingThumbnails ? 490 : 607,
      };
    }
    else {
      return {
        x: 150,
        y: 205,
        width: 704,
        height: isStudent ? 407 : isScrollingThumbnails ? 490 : 607,
      };
    }
  };

  // Add state for screen size
  const [screenDimensions, setScreenDimensions] = useState(getDefaultDimensions());

  useEffect(() => {
    if (open && data) {
      setCurrentSlide(0);
      const newDimensions = getDefaultDimensions();
      setScreenDimensions(newDimensions);
      setDimensions(newDimensions);
    }
  }, [open, data, bookId, pageId]);

  useEffect(() => {
    const handleResize = () => {
      const newDimensions = getDefaultDimensions();
      setScreenDimensions(newDimensions);
      setDimensions(newDimensions);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isStudent, isScrollingThumbnails]);

  const [isNavigating, setIsNavigating] = useState(false);
  const forwardBtnRef = useRef();
  const backBtnRef = useRef();
  const touchHandledForwardRef = useRef(false);
  const touchHandledBackRef = useRef(false);


  const checkKey = useCallback(
    debounceMath((e) => {
      if(isNavigating) return;
       e = e || window.event;
      if (e.keyCode == '37') {
        if (backBtnRef.current && !backBtnRef.current.disabled) {
          setIsNavigating(true);
          backBtnRef.current.click();
          setTimeout(() => setIsNavigating(false), 300);
        }
      } else if (e.keyCode == '39') {
        if (forwardBtnRef.current && !forwardBtnRef.current.disabled) {
          setIsNavigating(true);
          forwardBtnRef.current.click();
          setTimeout(() => setIsNavigating(false), 300);
        }
      }

    }), [isNavigating])


  useEffect(() => {
    document.addEventListener('keydown', checkKey);
    return () => {
      document.removeEventListener('keydown', () => {});
    };
  }, []);

  useEffect(() => {
    if (toogleButtonSlides && isTeacher) {
      const activeSlideIndex = sharedData?.currentPage ?? currentSlide;
      setTimeout(() => {
        scrollToActiveThumbnail(activeSlideIndex);
      }, 200);
    }
  }, [currentSlide, sharedData?.currentPage, toogleButtonSlides, isTeacher]);

  const openSlideWidget = useMemo(() => {
    if(currentUser?.role === 'teacher'){
      return open || filterShareSlide?.id
    }else{
      return filterShareSlide?.id;
    }
  }, [currentUser, filterShareSlide, open])

  const isToolbarCompact = window.innerWidth < 520 || (dimensions?.width != null && dimensions.width < 480);

  
  const fetchSharedData = async () => {
    try {
      const emptyData =
        Object?.keys(currentSlideData).length === 0 ||
        getSlideData?.pages?.length === 0 ||
        !getSlideData?.pages;
      if (emptyData && openSlideWidget && filterShareSlide?.id) {
        if (filterShareSlide?.data?.completeData?.book_id) {
          const response = await dispatch(
            handleGetAllSlides(filterShareSlide?.data?.completeData?.book_id),
          );
          const filterPage = response?.find(
            (item) => item?.page_id === filterShareSlide?.data?.completeData?.page_id,
          );
          const widgetData = {
            open: true,
            pageId: filterPage?.page_id,
            bookId: filterPage?.book_id,
            data: filterPage,
          };
          await dispatch(openSlideWidgetAction(widgetData));
        }
      } else {
        return;
      }
    } catch (error) {
      console.log(error, 'error123');
    }
  };

  useEffect(() => {
    fetchSharedData();
  }, [openSlideWidget]);

  useEffect(() => {
    return () => {
      if (shareClickTimeoutRef.current) {
        clearTimeout(shareClickTimeoutRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

 
  return (
    <SentryWrap>
      <Snackbar
        open={alertState.open}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alertState.severity} sx={{ width: '100%' }}>
          {alertState.message}
        </Alert>
      </Snackbar>
      {openSlideWidget && (
        <Container className="allCheckListSEction wigetsContainer disable-key-navigation">
          <div className="mainCheckList h-full">
            <ClickOutside onClickOutside={() => {
              setIsDragging(false);
            }}
            isOpen={openSlideWidget}
            > 
              
              <ResizeAndDragabbleSlide
                center={true}
                defaultValue={screenDimensions}
                onFocusValue={isScrollingThumbnails}
                minHeight={isStudent ? 240 : window.innerWidth < 768 ? 370 : 340}
                minWidth={isStudent ? 270 : window.innerWidth < 520 ? window.innerWidth * 0.6 : window.innerWidth < 768 ? 370 : 270}
                resize={(data) => {
                  setDimensions(data);
                  const newHeight = data.height;
                  let newThumbnailSize = Math.max(50, Math.min(180, newHeight / 5));
                  setThumbnailSize(newThumbnailSize);
                }}
                showAccordin={showAccordin}
                page={"text"}
                aspectRatio={true}
              >
                <div
                  data-drag-handle
                  className={`checkListTopSection setContentHide ${isDragging && showAccordin ? "widgetMobAndTab" : ""
                    }`}
                  style={{
                    position: "relative",
                    boxShadow: showAccordin && "rgba(0, 0, 0, 0.2) 0px 2px 4px",
                    width: "100%",
                    height: isMobileDevice() ? "auto" : "100%",
                    padding: "0px",
                  }}
                >
                  <div
                    className={`111 checkListFirstTxt widgetFooterGradientOrange bg-blue-10 h-44px pt-17px pb-14px fixed-header-text ${showAccordin ? "br-topLeft-topRight-15px" : "br-20px"
                      } `}
                  >
                    <AccordinButton
                      open={showAccordin}
                      onClose={() => {
                        setIsDragging(false);
                        setShowAccordin((prev) => !prev);
                      }}
                      shareableWidget={sharableWidget}
                    />

                    <span className={`text-white text-ft12-700 ${isTeacher ? "presentationMarginMedia" : ""}`}>Presentation</span>

                    {isTeacher && (
                        <div className="presentattionButtons" style={{ display: 'flex', alignItems: 'center', gap: isToolbarCompact ? 0 : 4 }}>
                          {isToolbarCompact ? (
                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                              <StyledIconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleSettingsMenuOpen(e);
                                }}
                                onTouchStart={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleSettingsMenuOpen(e);
                                }}
                                title="Settings"
                              >
                                <SettingsIcon />
                              </StyledIconButton>
                              <div onTouchStart={handleTouchShare}>
                                <StyledIconButton
                                  onClick={() => {
                                    const closeShareData = { ...filterShareSlide, status: false, message: "widget-slide" };
                                    if (filterShareSlide?.id) createCollectionWidgets(closeShareData);
                                    else sharePPT();
                                  }}
                                  onTouchStart={handleTouchShare}
                                  style={{ background: filterShareSlide?.status ? "green" : "transparent" }}
                                >
                                  <ShareIcon />
                                </StyledIconButton>
                              </div>
                              {Boolean(settingsMenuAnchor) && (
                                <ClickOutside onClickOutside={handleSettingsMenuClose}>
                                  <SettingsDropdownMenu>
                                    <DownloadMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        setExportIncludeNotes(true);
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        setExportIncludeNotes(true);
                                      }}>
                                      Export with Notes
                                    </DownloadMenuItem>
                                    <DownloadMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        setExportIncludeNotes(false);
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        setExportIncludeNotes(false);
                                      }}>
                                      Export without Notes
                                    </DownloadMenuItem>
                                    {download_ppt && (
                                      <>
                                        <DownloadMenuDivider />
                                        <DownloadMenuItem
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleSettingsMenuClose();
                                            handleDownloadWithNotes();
                                          }}
                                          onTouchStart={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleSettingsMenuClose();
                                            handleDownloadWithNotes();
                                          }}>
                                          Download with Notes
                                        </DownloadMenuItem>
                                        <DownloadMenuItem
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleSettingsMenuClose();
                                            handleDownloadWithoutNotes();
                                          }}
                                          onTouchStart={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            handleSettingsMenuClose();
                                            handleDownloadWithoutNotes();
                                          }}>
                                          Download without Notes
                                        </DownloadMenuItem>
                                      </>
                                    )}
                                    <DownloadMenuDivider />
                                    <DownloadMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        handlePrintWithNotes();
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        handlePrintWithNotes();
                                      }}>
                                      Print with Notes
                                    </DownloadMenuItem>
                                    <DownloadMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        handlePrintWithoutNotes();
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSettingsMenuClose();
                                        handlePrintWithoutNotes();
                                      }}>
                                      Print without Notes
                                    </DownloadMenuItem>
                                  </SettingsDropdownMenu>
                                </ClickOutside>
                              )}
                              <div style={{ display: 'none' }}>
                                <ExportToSlides
                                  ref={exportToSlidesRef}
                                  pptxBlob={pptxBlob}
                                  handleExportDone={() => setPptxBlob(null)}
                                  showAlert={showAlert}
                                  generateBlob={async (includeNotes) => await generatePPT(includeNotes, false)}
                                />
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <StyledIconButton
                                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleExportMenuOpen(e); }}
                                  onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handleExportMenuOpen(e); }}
                                  disabled={isDownloading}
                                  title="Export to Google Slides"
                                >
                                  <SlidesIcon />
                                </StyledIconButton>
                                {Boolean(exportMenuAnchor) && (
                                  <ClickOutside onClickOutside={handleExportMenuClose}>
                                    <DownloadDropdownMenu>
                                      <DownloadMenuItem onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleExportMenuClose(); setExportIncludeNotes(true); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handleExportMenuClose(); setExportIncludeNotes(true); }}>Export with Notes</DownloadMenuItem>
                                      <DownloadMenuDivider />
                                      <DownloadMenuItem onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleExportMenuClose(); setExportIncludeNotes(false); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handleExportMenuClose(); setExportIncludeNotes(false); }}>Export without Notes</DownloadMenuItem>
                                    </DownloadDropdownMenu>
                                  </ClickOutside>
                                )}
                                <div style={{ display: 'none' }}>
                                  <ExportToSlides ref={exportToSlidesRef} pptxBlob={pptxBlob} handleExportDone={() => setPptxBlob(null)} showAlert={showAlert} generateBlob={async (includeNotes) => await generatePPT(includeNotes, false)} />
                                </div>
                              </div>
                              {download_ppt && (
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                  <StyledIconButton
                                    className="doenloadUl"
                                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDownloadMenuOpen(e); }}
                                    onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handleDownloadMenuOpen(e); }}
                                    disabled={isDownloading}
                                  >
                                    {isDownloading && <CircularProgress size={18} style={{ color: 'white' }} />}
                                    <DownloadIcon />
                                  </StyledIconButton>
                                  {Boolean(downloadMenuAnchor) && (
                                    <ClickOutside onClickOutside={handleDownloadMenuClose}>
                                      <DownloadDropdownMenu>
                                        <DownloadMenuItem onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDownloadMenuClose(); handleDownloadWithNotes(); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handleDownloadMenuClose(); handleDownloadWithNotes(); }}>Download with Notes</DownloadMenuItem>
                                        <DownloadMenuDivider />
                                        <DownloadMenuItem onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDownloadMenuClose(); handleDownloadWithoutNotes(); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handleDownloadMenuClose(); handleDownloadWithoutNotes(); }}>Download without Notes</DownloadMenuItem>
                                      </DownloadDropdownMenu>
                                    </ClickOutside>
                                  )}
                                </div>
                              )}
                              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                <StyledIconButton onClick={(e) => { e.stopPropagation(); e.preventDefault(); handlePrintMenuOpen(e); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handlePrintMenuOpen(e); }} title="Print Slides">
                                  <PrintIcon />
                                </StyledIconButton>
                                {Boolean(printMenuAnchor) && (
                                  <ClickOutside onClickOutside={handlePrintMenuClose}>
                                    <DownloadDropdownMenu>
                                      <DownloadMenuItem onClick={(e) => { e.stopPropagation(); e.preventDefault(); handlePrintWithNotes(); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handlePrintWithNotes(); }}>Print with Notes</DownloadMenuItem>
                                      <DownloadMenuDivider />
                                      <DownloadMenuItem onClick={(e) => { e.stopPropagation(); e.preventDefault(); handlePrintWithoutNotes(); }} onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handlePrintWithoutNotes(); }}>Print without Notes</DownloadMenuItem>
                                    </DownloadDropdownMenu>
                                  </ClickOutside>
                                )}
                              </div>
                              <div onTouchStart={handleTouchShare}>
                                <StyledIconButton
                                  onClick={() => {
                                    const closeShareData = { ...filterShareSlide, status: false, message: "widget-slide" };
                                    if (filterShareSlide?.id) createCollectionWidgets(closeShareData);
                                    else sharePPT();
                                  }}
                                  onTouchStart={handleTouchShare}
                                  style={{ background: filterShareSlide?.status ? "green" : "transparent" }}
                                >
                                  <ShareIcon />
                                </StyledIconButton>
                              </div>
                            </>
                          )}
                        </div>
                    )}
                    {/* {isTeacher && (
                        <div
                          data-no-drag
                          style={{
                            position: "absolute",
                            zIndex: "9999",
                            right: "50px",
                            top: "7px",
                            pointerEvents: "auto",
                          }}
                        >
                        <StyledIconButton
                          data-no-drag
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (shareClickTimeoutRef.current) {
                              clearTimeout(shareClickTimeoutRef.current);
                            }
                            shareClickTimeoutRef.current = setTimeout(() => {
                              shareClickTimeoutRef.current = null;
                            }, 300);
                            const closeShareData = {
                              ...filterShareSlide,
                              status: false,
                              message: "widget-slide",
                            };
                            if (filterShareSlide?.id) {
                              createCollectionWidgets(closeShareData);
                            } else {
                              sharePPT();
                            }
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (shareClickTimeoutRef.current) {
                              clearTimeout(shareClickTimeoutRef.current);
                            }
                            shareClickTimeoutRef.current = setTimeout(() => {
                              shareClickTimeoutRef.current = null;
                            }, 300);
                            handleTouchShare(e);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          style={{
                            background: filterShareSlide?.status ? "green" : "transparent",
                            pointerEvents: "auto",
                            zIndex: 9999,
                            width: '25px',
                            height: '25px'
                          }}
                        >
                          <ShareIcon sx={{width:'20px', height:'20px'}} />
                        </StyledIconButton>
                      </div>
                    )} */}
                    {isTeacher && (
                      <StyledCancelIcon
                        data-no-drag
                          // onTouchStart={(e) => {
                          //   e.stopPropagation();
                          //   e.preventDefault();
                          //   handleClose();
                          // }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleClose();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        style={{
                          pointerEvents: "auto",
                          zIndex: 9999,
                        }}
                      />
                    )}
                  </div>


                  {showAccordin && (
                    <div
                      onMouseEnter={() => {
                      }}
                      onMouseLeave={() => {
                      }}
                      style={{
                        paddingRight: "14px",
                        paddingLeft: "14px",
                        paddingBottom: "33px",
                        paddingTop: "58px",
                      }}>
                      {isStudent && (
                        <div className="checkListTopSection ppt-widget-container">
                          {!sharedData?.pages?.id === 0 ? (
                            <NoActiveSlide>No active slide on this page</NoActiveSlide>
                          ) : (
                            <>
                              {sharedData?.pages?.src && (
                                <>
                                  <MainContent isStudent={isStudent}>
                                    <SlideContainer>
                                        <SlideWrapper size={thumbnailSize} isStudent={isStudent}>
                                        <SlideContent>
                                          {toogleButton ? (
                                            <NotesContainer className="w-full data-no-drag">
                                              {currentSlideData?.notes ? (
                                                currentSlideData?.notes
                                              ) : (
                                                <div className="text-center">
                                                  No active notes
                                                </div>
                                              )}
                                            </NotesContainer>
                                          ) : (
                                            <SlideImageHighlighterWidget
                                              imageUrl={sharedData?.pages?.src || ""}
                                              pageId={sharedData?.completeData?.page_id}
                                              bookId={sharedData?.completeData?.book_id}
                                              getBookSlides={sharedData?.completeData}
                                              activeSlide={
                                                sharedData?.currentPage
                                                  ? sharedData?.currentPage
                                                  : currentSlide
                                              }
                                              rectangles={sharedData?.pages?.data}
                                              setImageConfiguration={
                                                setImageConfiguration
                                              }
                                            />
                                          )}
                                        </SlideContent>
                                      </SlideWrapper>
                                    </SlideContainer>
                                  </MainContent>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {isTeacher && (
                        <div className="checkListTopSection ppt-widget-container">
                          {Object?.keys(currentSlideData).length === 0 ||
                            getSlideData?.pages?.length === 0 ||
                            !getSlideData?.pages ? (
                            <NoActiveSlide>No active slide on this page</NoActiveSlide>
                          ) : (
                            <>
                              {currentSlideData?.src && (
                                <>
                                  <MainContent>
                                    <SlideContainer>
                                        <SlideWrapper size={thumbnailSize}>
                                        <SlideContent>
                                          {(isTeacher && !toogleButton && currentSlide!=0) && (
                                            <NavigationArrow 
                                              ref={backBtnRef} 
                                              direction="left" 
                                              onClick={(e) => {
                                                if (touchHandledBackRef.current) {
                                                  return;
                                                }
                                                e.stopPropagation();
                                                handlePrevSlide();
                                              }} 
                                              onTouchEnd={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                touchHandledBackRef.current = true;
                                                handlePrevSlide();
                                                setTimeout(() => {
                                                  touchHandledBackRef.current = false;
                                                }, 300);
                                              }}
                                            >
                                              <ArrowBackIosIcon />
                                            </NavigationArrow>
                                          )}
                                          {toogleButton ? (
                                            <NotesContainer className="w-full" style={{ fontSize: `${fontSize}px` }}  data-no-drag>
                                              {currentSlideData?.notes ? (
                                              <MessageRenderer data={currentSlideData?.notes} />
                                              ) : (
                                                <div className="text-center">
                                                  No active notes
                                                </div>
                                              )}
                                            </NotesContainer>
                                          ) : (
                                            <SlideImageHighlighterWidget
                                              imageUrl={currentSlideData?.src || ""}
                                              pageId={pageId}
                                              bookId={bookId}
                                              getBookSlides={getSlideData}
                                              activeSlide={
                                                sharedData?.currentPage
                                                  ? sharedData?.currentPage
                                                  : currentSlide
                                              }
                                              rectangles={currentSlideData?.data}
                                              setImageConfiguration={
                                                setImageConfiguration
                                              }
                                            />
                                          )}

                                          {isTeacher && !toogleButton && currentSlide!=getSlideData?.pages?.length-1 && (
                                            <NavigationArrow 
                                              ref={forwardBtnRef} 
                                              direction="right" 
                                              onClick={(e) => {
                                                if (touchHandledForwardRef.current) {
                                                  return;
                                                }
                                                e.stopPropagation();
                                                handleNextSlide();
                                              }} 
                                              onTouchEnd={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                touchHandledForwardRef.current = true;
                                                handleNextSlide();
                                                setTimeout(() => {
                                                  touchHandledForwardRef.current = false;
                                                }, 300);
                                              }}
                                            >
                                              <ArrowForwardIosIcon />
                                            </NavigationArrow>
                                          )}
                                        </SlideContent>
                                      </SlideWrapper>
                                    </SlideContainer>
                                  </MainContent>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                      <ShowNotes
                                        setToogleButton={(val) => {
                                          onNotesClick(val);
                                          setToogleButton(val)
                                        }}
                                        toogleButton={toogleButton}
                                        currentSlide={currentSlideData}
                                        setIsScrollingThumbnails={setIsScrollingThumbnails}
                                      />
                                      <FormControlLabel
                                        classes={{
                                          root: StyledFormControlLabel,
                                          label: StyledFormControlLabel.label,
                                        }}
                                        style={{ width: 'fit-content',marginLeft:0 }}
                                        control={
                                          <Switch
                                            classes={{
                                              sizeSmall: StyledSwitch,
                                              checked: StyledSwitch,
                                            }}
                                            checked={toogleButtonSlides}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              setToogleButtonSlides(!toogleButtonSlides);
                                            }}
                                            name="En - Sp"
                                            color="primary"
                                            size="small"
                                          />
                                        }
                                        label="Show Slides"
                                      />
                                    </div>
                                    {toogleButton && (
                                      <FontSizeControls>
                                        <button 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            decreaseFontSize();
                                          }} 
                                          onTouchStart={(e) => {
                                            e.stopPropagation();
                                            decreaseFontSize();
                                          }}
                                        >
                                          A-
                                        </button>
                                        <button 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            increaseFontSize();
                                          }} 
                                          onTouchStart={(e) => {
                                            e.stopPropagation();
                                            increaseFontSize();
                                          }}
                                        >
                                          A+
                                        </button>
                                      </FontSizeControls>
                                    )}
                                  </div>
                                  {toogleButtonSlides && isTeacher ? (
                                    <SlidCont>
                                        <SliderContainer
                                          ref={sliderContainerRef}
                                          className="no-drag-inside-slider"
                                          onTouchStart={(e) => {
                                            if (e.target.closest('.no-drag-inside-slider[data-no-drag]')) {
                                              return;
                                            }
                                            setIsScrollingThumbnails(true);
                                          }}
                                          onTouchMove={(e) => {
                                            if (e.target.closest('.no-drag-inside-slider[data-no-drag]')) {
                                              e.stopPropagation();
                                              return;
                                            }
                                            e.stopPropagation();
                                          }}
                                          onTouchEnd={(e) => {
                                            if (e.target.closest('.no-drag-inside-slider[data-no-drag]')) {
                                              e.stopPropagation();
                                              return;
                                            }
                                            setTimeout(() => {
                                              setIsScrollingThumbnails(false);
                                            }, 100);
                                          }}
                                          onScroll={(e) => {
                                            e.stopPropagation();
                                          }}
                                       >
                                        <ThumbnailsWrapper>
                                          {getSlideData?.pages?.map((slide, index) => (
                                            <>
                                              {slide?.src ? (
                                                <SlideThumbnail
                                                  key={slide?.id}
                                                  ref={(el) => {
                                                    if (el) {
                                                      thumbnailRefs.current[index] = el;
                                                    }
                                                  }}
                                                  slide={slide}
                                                  index={index}
                                                  isActive={
                                                    (sharedData?.currentPage ??
                                                      currentSlide) === index
                                                  }
                                                  onSelect={handleActiveSlide}
                                                  AllData={getSlideData}
                                                  thumbnailSize={thumbnailSize}
                                                  shareClickTimeoutRef={shareClickTimeoutRef}
                                                />
                                              ) : null}
                                            </>
                                          ))}
                                        </ThumbnailsWrapper>
                                      </SliderContainer>
                                    </SlidCont>
                                    ) :
                                  
                                      (
                                        <p style={{fontSize:20,Color:'black',marginTop:20,textAlign:'center'}}>All the slides are hidden.</p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="widgetFooter widgetFooterGradientOrange"><label>Widget</label></div>
                </div>
              </ResizeAndDragabbleSlide>
            </ClickOutside>
          </div>
        </Container>
      )}
    </SentryWrap>
  );
};

const SlideThumbnail = React.forwardRef(({ slide, index, isActive, onSelect, AllData, thumbnailSize, shareClickTimeoutRef }, ref) => {
  const touchStartRef = React.useRef({ x: 0, y: 0, time: 0 });
  const isTouchingRef = React.useRef(false);
  
  return (
    <ThumbnailContainer
      ref={ref}
      className="no-drag-inside-slider"
      data-no-drag
      size={thumbnailSize}
      style={{ marginRight: index === AllData?.pages?.length - 1 ? "20px" : "0px" }}
      isActive={isActive}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onSelect(index, slide);
      }}
      onTouchStart={(e) => {
        isTouchingRef.current = true;
        touchStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          time: Date.now()
        };
        e.stopPropagation();
      }}
      onTouchMove={(e) => {
        if (!isTouchingRef.current) {
          return;
        }
        if (touchStartRef.current.x !== 0) {
          const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
          const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
          
          if (deltaX > 10 || deltaY > 10) {
            isTouchingRef.current = false;
            touchStartRef.current = { x: 0, y: 0, time: 0 };
            return;
          }
        }
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        if (!isTouchingRef.current) {
          touchStartRef.current = { x: 0, y: 0, time: 0 };
          return;
        }
        if (touchStartRef.current.x !== 0) {
          const deltaX = Math.abs(e.changedTouches[0].clientX - touchStartRef.current.x);
          const deltaY = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
          const deltaTime = Date.now() - touchStartRef.current.time;
          
          if (deltaX < 10 && deltaY < 10 && deltaTime < 300) {
            e.stopPropagation();
            e.preventDefault();
            onSelect(index, slide);
          }
          
          touchStartRef.current = { x: 0, y: 0, time: 0 };
        }
        isTouchingRef.current = false;
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}

    >
      <ThumbnailContent>
        {slide?.src ? (
          <ImageSlide
            className="prevent-select"
            src={slide?.src ? getImageThumbPrimaryUrl(slide.src) : undefined}
            alt="Slide thumbnail"
          />
        ) : null}
      </ThumbnailContent>
    </ThumbnailContainer>

  );
});

export default SlideWidgetComponent;

const ShowNotes = ({ toogleButton, setToogleButton, currentSlide, setIsScrollingThumbnails }) => {
  return (
    <StyledFormControlLabel
      style={{ width: 'fit-content', marginLeft: 0, marginTop: 5 }}
      control={
        <StyledSwitch
          disabled={!currentSlide || currentSlide === undefined || currentSlide?.notes === ""}
          checked={toogleButton}
          onChange={(e) => {
            e.stopPropagation();
            if (!currentSlide || currentSlide === undefined || currentSlide?.notes === "") {
              setToogleButton(false);
            }
            setToogleButton(!toogleButton);
          }}
          // onTouchStart={(e) => {
          //   e.preventDefault();
          //   e.stopPropagation();
          //   if (!currentSlide || currentSlide === undefined || currentSlide?.notes === "") {
          //     setToogleButton(false);
          //     console.log("toggleButton 1000", toogleButton);
          //   }
          //   setToogleButton(!toogleButton);
          //   console.log("toggleButton 1001", !toogleButton);
          // }}
          name="En - Sp"
          color="primary"
          size="small"
        />
      }
      label="Show Notes"
    />
  );
};

const NotesContainer = styled.div`
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  overscroll-behavior: contain;
  text-align: start;
  padding: 20px 40px;
  @media (max-width: 1500px){
    padding-left:45px;
  }
  
  @media (max-width: 432px){
    padding-left:35px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  position: absolute;
  width: 100%;
  top: 0px;
  color: white;
  height:36px;
  background:#012b70;
  border-top-left-radius:20px;
  border-top-right-radius:20px;
`;

const TeacherNote = styled.div`
  background-color: #012b70;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  width: 120px;
  padding-left: 10px;
  padding-top: 5px;
`;

const BlueContainer = styled.div`
  background-color: #012b70;
  padding: 3px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  cursor: "pointer"
`;

const BlueContainerShare = styled(BlueContainer)`
  display:flex;
  align-items:center;
  justify-content: center;
  padding:4px 5px;
  margin-right:5px;
`;
const MainContent = styled.div`
  flex: 1;
  padding: 5px;
  margin-top: ${(props) => (props.isStudent ? "0px" : "0px")};
`;

const SlideContainer = styled.div`
  // max-width: 571px;
  margin: 0 auto;
  padding: 6px; // Added padding
  background: white;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
`;

const SlideWrapper = styled.div`
  aspect-ratio: ${(props) => {
    if (props.isStudent) {
      // Aspect ratios for teachers
      if (props.size < 60) return "16 / 6";
      if (props.size < 90) return "16 / 7";
      if (props.size < 130) return "16 / 8";
      if (props.size < 300) return "16 / 8";
      return "16 / 8";
    } else {
      if (props.size < 60) return "16 / 4";
      if (props.size < 90) return "16 / 5";
      if (props.size < 130) return "16 / 8";
      if (props.size < 300) return "16 / 9";
      return "16 / 9";
    }
  }};
  
  border-radius: 0.75rem;
  position: relative;
`;



const SlideContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;
const SliderContainer = styled.div`
  max-width:98%;
  margin:auto;
  display: flex;
  gap: 1rem;
  padding: 10px 10px;
  background: white;
  border-top: 1px solid #e5e7eb;
  overflow-x: auto; 
  overflow-y: hidden;
  max-height: 163px;
  border-radius: 20px;
  scrollbar-gutter: stable both-edges;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  overscroll-behavior-x: contain;
  touch-action: pan-x;
  will-change: scroll-position;
  &::-webkit-scrollbar {
        width: 3px;
        height: 6px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: darkgrey;
        outline: 1px solid slategrey;
      }
`;
const SlidCont = styled.div`
  margin-bottom:7px;
`;

const ImageSlide = styled.img`
  width: 100%;
  // height: 100%;
  object-fit: contain;
`;

const ThumbnailsWrapper = styled.div`
  display: flex;
  gap: 5px;
  touch-action: pan-x;
  will-change: transform;
`;

const ThumbnailContainer = styled.div`
  position: relative;
  // min-width: fit-content;
  // aspect-ratio: 16/9;
  background: white;
  border-radius: 0.5rem;
  cursor: grab;
  overflow: hidden;
  border: 2px solid ${(props) => (props.isActive ? "#03989E" : "transparent")};
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  transform: ${(props) => (props.isDraggedOver ? "scale(1.05)" : "scale(1)")};
  transition: all 0.2s ease;
  touch-action: pan-x;
  pointer-events: auto;
//  width: 70px;
//   height:  40px;
width: ${(props) => Math.max(props.size, 70)}px;  
height: ${(props) => Math.max(props.size - 45, 50)}px;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
`;

const ThumbnailContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background:lightgrey;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  // min-height: 500px;
  // background-color: rgb(58, 58, 58);
  position: relative;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 0px;
  z-index: 99999999;
  position: fixed;
  cursor: grab;
  // left: 25%;
  // margin: 15px auto;/
  // transform: translate(31%, 0%);

  @media (max-width: 1450px) {
    // width: 96%;
    // transform: translate(0%, 0%);
  }
  @media (max-width: 992px) {
    // width: 95%;
    // transform: translate(0%, 0%);
  }
`;

const NoActiveSlide = styled.div`
  color: red;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const FontSizeControls = styled.div`
  display: flex;
  gap: 5px;
  margin-right: 10px;

  button {
    background: #012b70;
    color: white;
    border: none;
    padding: 5px 10px;
    font-size: 14px;
    border-radius: 5px;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: #011f4b;
    }
  }
`;

const DownloadDropdownMenu = styled.ul`
  position: absolute;
  top: 100%;
  right: -80px;
  margin-top: 8px;
  min-width: 200px;
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  z-index: 100000000;
  list-style: none;
`;

const SettingsDropdownMenu = styled(DownloadDropdownMenu)`
  right: 0;
  max-height: 70vh;
  overflow-y: auto;
`;

const DownloadMenuItem = styled.li`
  padding: 10px 16px;
  cursor: pointer;
  color: #333;
  font-size: 14px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

const DownloadMenuDivider = styled.li`
  height: 1px;
  margin: 4px 0;
  background-color: rgba(0, 0, 0, 0.12);
  list-style: none;
  padding: 0;
`;